import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@material-ui/core';

import {
  checkIsPaymentScheduleOptionDisabled,
  countTaxedAmount,
} from 'modules/services/utils/billingUtils';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';

import { FieldInfo, NumberTextField, Select } from 'common/components/index';

import {
  BillingOptionType,
  FrequencyType,
  FrequencyTypes,
} from 'modules/services/constants/packages';
import { IEditablePackageConfiguration } from 'common/interfaces/membership';
import { IBillingOption } from 'modules/services/interfaces/packages';

import inputLabels from 'common/messages/inputLabels';
import servicesMessages from 'modules/services/messages/messages';
import { defaultNumberFormatProps } from 'common/components/NumberController/NumberController';
import { getAccessByPropPath } from 'common/utils/errorObject';

interface IAmountSection {
  membershipPackage: IEditablePackageConfiguration | null;
  billingOption: IBillingOption | null;
  isOutOfTerm: boolean;
}

const AmountSection = ({
  membershipPackage,
  billingOption,
  isOutOfTerm,
}: IAmountSection): JSX.Element => {
  const { control, formState, watch, setValue } = useFormContext();
  const { errors } = formState;
  const renderIntlMessage = useRenderIntlMessage();

  const { type: optionType, revenueCode } = billingOption || {};
  const { durationType, durationEditableNumber } = membershipPackage || {};

  const valuePath = isOutOfTerm ? 'outOfTermBillingOption.' : '';

  const totalAmountError = getAccessByPropPath(errors, `${valuePath}totalAmount.value`);

  const taxedTotalAmount = watch(`${valuePath}taxedTotalAmount`);

  const onTotalChange = value => {
    setValue(
      `${valuePath}taxedTotalAmount`,
      countTaxedAmount(Number(value), revenueCode?.totalTax || 0),
    );
  };

  return (
    <>
      <Grid item xs={6}>
        <Controller
          name={`${valuePath}totalAmount`}
          control={control}
          render={({ field }) => {
            const { name, value: totalAmount, onChange, onBlur } = field;
            return (
              <NumberTextField
                name={name}
                disabled={!totalAmount?.editable}
                fullWidth
                value={totalAmount?.value || 0}
                variant="outlined"
                label={<FormattedMessage {...inputLabels.totalAmount} />}
                onChange={value => {
                  onChange({ ...totalAmount, value });
                  onTotalChange(value);
                }}
                onBlur={onBlur}
                numberFormatProps={{
                  ...defaultNumberFormatProps,
                  prefix: '$',
                  decimalScale: 2,
                }}
                error={!!totalAmountError}
                helperText={renderIntlMessage(totalAmountError?.message, {
                  value:
                    totalAmount?.type === 'max' ? totalAmount?.maxValue : totalAmount?.minValue,
                })}
              />
            );
          }}
        />
      </Grid>

      <Grid item xs={6}>
        <FieldInfo
          inputMode
          grayedOut
          label={<FormattedMessage {...inputLabels.taxedTotalAmount} />}
          description={`$${taxedTotalAmount?.toFixed(2)}`}
        />
      </Grid>

      {optionType !== BillingOptionType.PAID_IN_FULL && (
        <>
          <Grid item xs={6}>
            <Controller
              name={`${valuePath}paymentEditableSchedule`}
              control={control}
              render={({ field }) => {
                const { value: paymentEditableSchedule, onChange } = field;

                return (
                  <Select
                    label={<FormattedMessage {...servicesMessages.paymentScheduleLabel} />}
                    variant="outlined"
                    error={!!errors.paymentEditableSchedule}
                    helperText={renderIntlMessage(errors.paymentEditableSchedule?.message)}
                    fullWidth
                    disabled={!paymentEditableSchedule?.editable}
                    value={paymentEditableSchedule?.value || ''}
                    onChange={scheduleValue => {
                      onChange({ ...paymentEditableSchedule, value: scheduleValue });
                    }}
                  >
                    {FrequencyTypes.getSelectOptions({
                      isDisabledFunc: (type: FrequencyType) => {
                        if (durationType) {
                          return checkIsPaymentScheduleOptionDisabled(
                            durationType,
                            Number(durationEditableNumber?.value),
                            type,
                          );
                        }

                        return false;
                      },
                    })}
                  </Select>
                );
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name={`${valuePath}pricePerBilling`}
              control={control}
              defaultValue={0}
              render={({ field }) => (
                <FieldInfo
                  inputMode
                  grayedOut
                  label={<FormattedMessage {...servicesMessages.pricePerBillingLabel} />}
                  description={`$${field.value?.toFixed(2)}`}
                />
              )}
            />
          </Grid>
        </>
      )}
    </>
  );
};

export default AmountSection;
